import React, { useState, useEffect, useCallback } from 'react';
import { DollarSign, Search, RefreshCw, CheckCircle, XCircle, Clock, ExternalLink } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { api } from '@/lib/api';
import { toast } from 'sonner';

const statusConfig = {
  approved: { label: 'Aprobado', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle },
  rejected: { label: 'Rechazado', color: 'bg-red-100 text-red-700', icon: XCircle },
  pending: { label: 'Pendiente', color: 'bg-amber-100 text-amber-700', icon: Clock },
  in_process: { label: 'En proceso', color: 'bg-blue-100 text-blue-700', icon: Clock },
  cancelled: { label: 'Cancelado', color: 'bg-slate-100 text-slate-600', icon: XCircle },
  refunded: { label: 'Reembolsado', color: 'bg-purple-100 text-purple-700', icon: RefreshCw },
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  try {
    const d = dateStr.includes('T') ? dateStr.split('T') : [dateStr];
    if (d[0]) {
      const [y, m, day] = d[0].split('-');
      const time = d[1] ? d[1].substring(0, 5) : '';
      return `${day}/${m}/${y}${time ? ` ${time}` : ''}`;
    }
    return dateStr;
  } catch { return dateStr; }
};

const PaymentsPanel = () => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState({});
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');

  const loadPayments = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, page_size: 50 });
      if (statusFilter !== 'all') params.append('status_filter', statusFilter);
      if (search) params.append('search', search);
      const res = await api.get(`/payments/list?${params}`);
      setPayments(res.data.items || []);
      setTotal(res.data.total || 0);
      setTotalPages(res.data.total_pages || 1);
      setStats(res.data.stats || {});
    } catch (e) {
      console.error('Error loading payments:', e);
      toast.error('Error al cargar pagos');
    } finally { setLoading(false); }
  }, [page, statusFilter, search]);

  useEffect(() => { loadPayments(); }, [loadPayments]);

  const approvedStats = stats.approved || { count: 0, total_amount: 0 };
  const pendingStats = stats.pending || { count: 0, total_amount: 0 };
  const rejectedStats = stats.rejected || { count: 0, total_amount: 0 };

  return (
    <div className="space-y-6" data-testid="payments-panel">
      {/* Stats cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="p-4 border-emerald-200 bg-emerald-50">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-emerald-700 font-medium">Aprobados</p>
              <p className="text-2xl font-bold text-emerald-800">${(approvedStats.total_amount || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}</p>
              <p className="text-xs text-emerald-600">{approvedStats.count || 0} pagos</p>
            </div>
            <CheckCircle className="w-8 h-8 text-emerald-500" />
          </div>
        </Card>
        <Card className="p-4 border-amber-200 bg-amber-50">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-amber-700 font-medium">Pendientes</p>
              <p className="text-2xl font-bold text-amber-800">${(pendingStats.total_amount || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}</p>
              <p className="text-xs text-amber-600">{pendingStats.count || 0} pagos</p>
            </div>
            <Clock className="w-8 h-8 text-amber-500" />
          </div>
        </Card>
        <Card className="p-4 border-red-200 bg-red-50">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-red-700 font-medium">Rechazados</p>
              <p className="text-2xl font-bold text-red-800">${(rejectedStats.total_amount || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}</p>
              <p className="text-xs text-red-600">{rejectedStats.count || 0} pagos</p>
            </div>
            <XCircle className="w-8 h-8 text-red-500" />
          </div>
        </Card>
      </div>

      {/* Filters */}
      <Card className="border-slate-200 bg-white overflow-hidden">
        <div className="p-4 border-b border-slate-200">
          <div className="flex flex-wrap justify-between items-center gap-3">
            <h2 className="text-lg font-semibold text-slate-900">Pagos recibidos ({total})</h2>
            <div className="flex items-center gap-2">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  placeholder="Buscar por cliente, ID o email..."
                  value={search}
                  onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                  className="pl-9 w-64"
                  data-testid="payments-search"
                />
              </div>
              <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
                <SelectTrigger className="w-[160px]" data-testid="payments-status-filter">
                  <SelectValue placeholder="Estado" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="approved">Aprobados</SelectItem>
                  <SelectItem value="pending">Pendientes</SelectItem>
                  <SelectItem value="rejected">Rechazados</SelectItem>
                  <SelectItem value="in_process">En proceso</SelectItem>
                  <SelectItem value="refunded">Reembolsados</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" size="sm" onClick={loadPayments} data-testid="payments-refresh">
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              </Button>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="payments-table">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Fecha</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Cliente</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Monto</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Estado</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Metodo</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Email pagador</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">ID MP</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {loading && payments.length === 0 ? (
                <tr><td colSpan={7} className="text-center py-12 text-slate-500">Cargando pagos...</td></tr>
              ) : payments.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center py-12">
                    <DollarSign className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500 font-medium">No hay pagos registrados</p>
                    <p className="text-xs text-slate-400 mt-1">Los pagos apareceran aqui cuando los clientes paguen via Mercado Pago</p>
                  </td>
                </tr>
              ) : (
                payments.map((payment) => {
                  const cfg = statusConfig[payment.status] || statusConfig.pending;
                  const StatusIcon = cfg.icon;
                  return (
                    <tr key={payment.id || payment.mercadopago_payment_id} className="hover:bg-slate-50" data-testid={`payment-row-${payment.mercadopago_payment_id}`}>
                      <td className="px-4 py-3 text-sm text-slate-700">{formatDate(payment.created_at)}</td>
                      <td className="px-4 py-3 text-sm">
                        <p className="font-medium text-slate-900">{payment.debtor_name || '-'}</p>
                        {payment.debtor_dni && <p className="text-xs text-slate-500">DNI: {payment.debtor_dni}</p>}
                      </td>
                      <td className="px-4 py-3 text-sm font-semibold text-slate-900">
                        ${(payment.amount || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <Badge className={`${cfg.color} gap-1`}>
                          <StatusIcon className="w-3 h-3" />
                          {cfg.label}
                        </Badge>
                        {payment.status_detail && (
                          <p className="text-[10px] text-slate-400 mt-0.5">{payment.status_detail}</p>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600">
                        {payment.payment_method || '-'}
                        {payment.payment_type && <span className="text-xs text-slate-400 ml-1">({payment.payment_type})</span>}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600">{payment.payer_email || '-'}</td>
                      <td className="px-4 py-3 text-sm">
                        <div className="flex items-center gap-1">
                          <code className="text-xs bg-slate-100 px-1.5 py-0.5 rounded font-mono">{payment.mercadopago_payment_id}</code>
                          <a
                            href={`https://www.mercadopago.com.ar/activities/detail/${payment.mercadopago_payment_id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-500 hover:text-blue-700"
                            title="Ver en Mercado Pago"
                          >
                            <ExternalLink className="w-3.5 h-3.5" />
                          </a>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="p-4 border-t border-slate-200 flex justify-between items-center">
            <p className="text-sm text-slate-600">Pagina {page} de {totalPages} ({total} pagos)</p>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>Anterior</Button>
              <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>Siguiente</Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
};

export default PaymentsPanel;
